{
  class InfiniteScroll extends HTMLElement {
    #abortController;
    #loading = false;
    #io;

    connectedCallback() {
      this.#abortController = new AbortController();
      const sentinel = this.querySelector('.infinite-scroll__sentinel');
      if (!sentinel) return;
      this.#io = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) this.#loadNext();
          }
        },
        { rootMargin: '600px 0px' }
      );
      this.#io.observe(sentinel);
    }

    disconnectedCallback() {
      this.#abortController?.abort();
      this.#io?.disconnect();
    }

    async #loadNext() {
      if (this.#loading) return;
      const nextUrl = this.getAttribute('next-url');
      if (!nextUrl) return;
      this.#loading = true;
      this.setAttribute('loading', '');
      try {
        const sectionId = this.getAttribute('section-id');
        const targetId = this.getAttribute('target');
        const url = new URL(nextUrl, location.origin);
        url.searchParams.set('section_id', sectionId);
        const response = await fetch(url.toString(), { signal: this.#abortController.signal });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');

        const targetList = document.getElementById(targetId);
        const sourceList = doc.getElementById(targetId);
        if (targetList && sourceList) {
          const fragment = document.createDocumentFragment();
          for (const card of sourceList.querySelectorAll(':scope > product-card')) {
            const imported = document.importNode(card, true);
            imported.removeAttribute('reveal-on-scroll');
            for (const el of imported.querySelectorAll('[reveal-on-scroll]')) {
              el.removeAttribute('reveal-on-scroll');
            }
            fragment.appendChild(imported);
          }
          targetList.appendChild(fragment);
        }

        const newSelf = doc.querySelector(`infinite-scroll[section-id="${sectionId}"]`);
        const newNextUrl = newSelf?.getAttribute('next-url') || '';
        if (newNextUrl) {
          this.setAttribute('next-url', newNextUrl);
        } else {
          this.removeAttribute('next-url');
          this.#io?.disconnect();
        }
      } catch (e) {
        if (e.name !== 'AbortError') {
          console.error('[infinite-scroll]', e);
          this.removeAttribute('next-url');
          this.#io?.disconnect();
        }
      } finally {
        this.#loading = false;
        this.removeAttribute('loading');
        const sentinel = this.querySelector('.infinite-scroll__sentinel');
        if (sentinel && this.getAttribute('next-url')) {
          const rect = sentinel.getBoundingClientRect();
          if (rect.top < window.innerHeight + 600) {
            this.#loadNext();
          }
        }
      }
    }
  }

  if (!window.customElements.get('infinite-scroll')) {
    window.customElements.define('infinite-scroll', InfiniteScroll);
  }
}
